import RichDialog from '../RichDialog/RichDialog';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  isOpen: boolean;
  closehandler: () => void;
  btn1handler: () => void;
}

const PrivacyCheckbox: React.FC<Props> = ({ isOpen, closehandler, btn1handler }) => {
  const { t, i18n } = useTranslation();

  return (
    <RichDialog
      visible={isOpen}
      btn1text={t('OK')}
      closehandler={closehandler}
      btn1handler={btn1handler}
      title={<div className="text-blue-500 text-xl mb-3 font-bold">{t('Privacy Policy')}</div>}
    >
      <div className="">
        <div className="font-bold">{t('Privacy Policy for Brainful Labs, SL')}</div>
        <div>
          {t(
            'At Chairingtool, accessible from chairingtool.com, we are committed to protecting the privacy of our users. This Privacy Policy explains what information we collect, how we use it, and your rights regarding your personal data.',
          )}
        </div>

        <div className="font-bold mt-4">{t('1. Scope')}</div>
        <div>
          {t(
            'This Privacy Policy applies to all users of our online service and covers personal information submitted, collected, or generated through Chairingtool. It does not apply to offline activities or to third-party services not controlled by us.',
          )}
        </div>

        <div className="font-bold mt-4">{t('2. Consent')}</div>
        <div>
          {t(
            'By creating an account or using Chairingtool, you consent to the collection and use of your information as described in this Privacy Policy.',
          )}
        </div>

        <div className="font-bold mt-4">{t('3. Information We Collect')}</div>
        <div>
          {t(
            'We collect information that you provide directly, information that is publicly available about researchers, and data automatically collected during your use of the Service.',
          )}
        </div>

        <div className="mt-2">{t('This includes:')}</div>
        <ul className="list-disc ml-6">
          <li>{t('Name, email address, country, and affiliation')}</li>
          <li>{t('Conference submission and review data')}</li>
          <li>{t('Information about your interactions with hosted conferences')}</li>
          <li>{t('Public researcher information such as ORCID, DBLP, or Google Scholar profiles')}</li>
          <li>{t('Authentication metadata from Google, GitHub, or ORCID when using OAuth login')}</li>
          <li>{t('Essential technical log information such as IP address, browser type, and timestamps')}</li>
        </ul>

        <div className="font-bold mt-4">{t('4. Cookies')}</div>
        <div>
          {t(
            'Chairingtool uses essential cookies only. These cookies are required for functions such as authentication and session management. We do not use analytics, tracking, or advertising cookies.',
          )}
        </div>

        <div className="font-bold mt-4">{t('5. Payment Information')}</div>
        <div>
          {t(
            'Payments are processed by Stripe. We do not store or process credit card or financial information on our servers.',
          )}
        </div>

        <div className="font-bold mt-4">{t('6. How We Use Your Information')}</div>
        <div>{t('We use the information we collect to:')}</div>
        <ul className="list-disc ml-6">
          <li>{t('Operate and maintain the Service')}</li>
          <li>{t('Manage conference submissions and reviews')}</li>
          <li>{t('Authenticate users and manage accounts')}</li>
          <li>{t('Communicate updates and service-related information')}</li>
          <li>{t('Ensure platform security and prevent fraud')}</li>
          <li>{t('Comply with legal obligations when necessary')}</li>
        </ul>

        <div className="font-bold mt-4">{t('7. Legal Basis for Processing (GDPR)')}</div>
        <div>
          {t(
            'We process personal data based on consent, performance of a contract, and our legitimate interests in maintaining the security and integrity of the platform.',
          )}
        </div>

        <div className="font-bold mt-4">{t('8. Data Sharing and Third-Party Services')}</div>
        <div>{t('We only share data with service providers essential to operating Chairingtool, including:')}</div>
        <ul className="list-disc ml-6">
          <li>{t('AWS (EU data centers) for hosting and data storage')}</li>
          <li>{t('Postmark for transactional email delivery')}</li>
          <li>{t('Stripe for payment processing')}</li>
          <li>{t('Google, GitHub, and ORCID for OAuth authentication')}</li>
        </ul>
        <div>
          {t(
            'These providers act as data processors under GDPR. We do not sell personal information to any third party.',
          )}
        </div>

        <div className="font-bold mt-4">{t('9. International Data Transfers')}</div>
        <div>
          {t(
            'All data is stored in European data centers. If a transfer outside the EU becomes necessary, we will apply appropriate safeguards such as Standard Contractual Clauses.',
          )}
        </div>

        <div className="font-bold mt-4">{t('10. Data Retention')}</div>
        <div>
          {t(
            'Conference-related information is retained for 10 years to preserve academic records. User accounts are kept until deletion is requested or after 10 years of inactivity.',
          )}
        </div>

        <div className="font-bold mt-4">{t('11. Co-Author and Submission Data')}</div>
        <div>
          {t(
            'When submitting materials containing other authors’ data, you are responsible for ensuring you have their permission. Co-authors must confirm their authorship through their own accounts.',
          )}
        </div>

        <div className="font-bold mt-4">{t('12. Data Security')}</div>
        <div>
          {t(
            'We implement security measures including encrypted connections, secured APIs with JWT authentication, limited internal access, and logging for security purposes. Data may only be released when required by law, such as a judicial order.',
          )}
        </div>

        <div className="font-bold mt-4">{t('13. User Rights (GDPR)')}</div>
        <div>{t('Users have the following rights:')}</div>
        <ul className="list-disc ml-6">
          <li>{t('Right to access')}</li>
          <li>{t('Right to rectification')}</li>
          <li>{t('Right to erasure')}</li>
          <li>{t('Right to restrict processing')}</li>
          <li>{t('Right to object')}</li>
          <li>{t('Right to data portability')}</li>
          <li>{t('Right to withdraw consent at any time')}</li>
        </ul>
        <div>
          {t(
            'Requests will be responded to within one month. You can contact us at support@chairingtool.com to exercise your rights.',
          )}
        </div>

        <div className="font-bold mt-4">{t('14. CCPA/CPRA Rights')}</div>
        <div>
          {t(
            'California residents may request access to their personal data, request deletion, request details about data sharing, and opt out of the sale of personal data (we do not sell personal data).',
          )}
        </div>

        <div className="font-bold mt-4">{t('15. Account and Data Deletion')}</div>
        <div>
          {t(
            'Users may delete their submissions and account directly through the platform. Some information may be retained where required to comply with legal obligations or maintain conference records.',
          )}
        </div>

        <div className="font-bold mt-4">{t('16. Changes to This Privacy Policy')}</div>
        <div>
          {t(
            'We may update this Privacy Policy periodically. Updates will be posted on this page with an updated “Last Updated” date.',
          )}
        </div>
        <div className="font-bold mt-4">{t('17. Contact Us')}</div>
        <div>
          {t(
            'If you have questions or concerns about this Privacy Policy, please contact us at support@chairingtool.com.',
          )}
        </div>
        <div className="mt-4 italic">Last Updated: November 2025</div>
      </div>
    </RichDialog>
  );
};

export default PrivacyCheckbox;
